export type ExternalSelectOption = {
    value: string;
    label: string;
    title: string;
};

/**
 * Provides utility methods for mapping external endpoint payloads into Select options.
 */
export class ExternalSelectOptionMapper {
    public static mapItems(items: unknown[], valueField: string, labelField: string): ExternalSelectOption[] {
        return items
            .map(item => {
                if (!item || typeof item !== 'object') {
                    return;
                }

                const record = item as Record<string, unknown>;
                const value = record[valueField];
                const label = record[labelField];

                if (value == null || label == null) {
                    return;
                }

                const valueString = typeof value === 'string' ? value : String(value);
                const labelString = typeof label === 'string' ? label : String(label);

                return { value: valueString, label: labelString, title: labelString };
            })
            .filter((option): option is ExternalSelectOption => Boolean(option));
    }

    public static mapArrayData(items: unknown[], valueField: string, labelField: string): ExternalSelectOption[] {
        return ExternalSelectOptionMapper.mapItems(items, valueField, labelField);
    }

    public static mapObjectData(
        data: unknown,
        itemsPath: unknown,
        valueField: string,
        labelField: string
    ): ExternalSelectOption[] {
        if (!data || typeof data !== 'object') {
            console.error('data is required to be an object when mapping remote select options.');
            return [];
        }

        const record = data as Record<string, unknown>;

        if (typeof itemsPath !== 'string' || itemsPath.length === 0) {
            console.error('itemsPath is required when data is an object.');
            return [];
        }

        const items = record[itemsPath];

        if (!Array.isArray(items)) {
            console.error('items is not an array.');
            return [];
        }

        return ExternalSelectOptionMapper.mapItems(items, valueField, labelField);
    }

    public static mapRemoteData(
        data: unknown,
        itemsPath: unknown,
        valueField: string,
        labelField: string
    ): ExternalSelectOption[] {
        if (!data) {
            return [];
        }

        if (Array.isArray(data)) {
            return ExternalSelectOptionMapper.mapArrayData(data, valueField, labelField);
        }

        if (typeof data === 'object') {
            return ExternalSelectOptionMapper.mapObjectData(data, itemsPath, valueField, labelField);
        }

        console.error('Unsupported data type: ', typeof data);
        return [];
    }
}
